import { computed, onScopeDispose, ref, toValue, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAbortableRequest } from './useAbortableRequest';
import GgSupportStateAPI from 'dashboard/api/ggSupportState';

const PENDING = ['queued', 'waiting', 'processing', 'unknown'];
const POLL_INTERVAL = 3000;

export function useGgSupportState(conversationId, ticketId, enabled) {
  const { t, te } = useI18n();
  const current = ref(null);
  const operation = ref(null);
  const error = ref('');
  const submitting = ref(false);
  const dialog = ref(null);
  const lookup = useAbortableRequest();
  const recovery = useAbortableRequest();
  const intent = ref(null);
  const pending = computed(() => PENDING.includes(operation.value?.state));
  const busy = computed(() => submitting.value || lookup.isPending.value);
  const blocked = computed(() => operation.value?.state === 'blocked');
  const noteFailed = computed(
    () =>
      operation.value?.state === 'saved' &&
      operation.value.note_status === 'failed'
  );
  let timer;
  let generation = 0;
  let disposed = false;

  const refresh = async () => {
    if (!toValue(enabled) || !toValue(ticketId)) return;
    current.value = null;
    error.value = '';
    try {
      const response = await lookup.run(signal =>
        GgSupportStateAPI.current(toValue(conversationId), toValue(ticketId), {
          signal,
        })
      );
      if (response) current.value = response.data;
    } catch (exception) {
      error.value =
        exception.response?.data?.code || 'SUPPORT_STATE_UNAVAILABLE';
    }
  };

  const apply = async record => {
    const previous = operation.value;
    const relevant =
      record &&
      (record.payload.ticket_id === toValue(ticketId) ||
        PENDING.includes(record.state) ||
        record.state === 'blocked' ||
        (record.state === 'saved' && record.note_status !== 'done'));
    operation.value = relevant ? record : null;
    if (record?.operation_id === intent.value?.operation_id)
      intent.value = null;
    if (
      relevant &&
      ['saved', 'failed'].includes(record.state) &&
      (previous?.operation_id !== record.operation_id ||
        previous?.state !== record.state)
    ) {
      if (previous && record.state === 'saved')
        useAlert(
          t('GG_SUPPORT_STATE.SAVED', {
            code: record.result.ticket.ticket_code,
          })
        );
      if (previous && record.state === 'failed') {
        const key = `GG_SUPPORT_STATE.ERRORS.${record.last_error_code}`;
        useAlert(t(te(key) ? key : 'GG_SUPPORT_STATE.ERRORS.REQUEST_FAILED'));
      }
      await refresh();
    }
  };

  const poll = async operationId => {
    const version = generation;
    try {
      const response = await recovery.run(signal =>
        GgSupportStateAPI.operation(toValue(conversationId), operationId, {
          signal,
        })
      );
      if (response && !disposed && version === generation)
        await apply(response.data.operation);
    } catch {
      error.value = 'STATUS_UNAVAILABLE';
    } finally {
      clearTimeout(timer);
      const record = operation.value;
      if (
        !disposed &&
        version === generation &&
        record &&
        (PENDING.includes(record.state) ||
          (record.state === 'saved' && record.note_status === 'pending'))
      ) {
        timer = setTimeout(() => poll(record.operation_id), POLL_INTERVAL);
      }
    }
  };

  const open = action => {
    if (
      busy.value ||
      pending.value ||
      blocked.value ||
      !current.value?.allowed_actions.includes(action)
    )
      return;
    dialog.value = {
      action,
      snapshot: current.value,
      closingText:
        action === 'complete' && operation.value?.state === 'failed'
          ? operation.value.payload.closing_text || ''
          : '',
    };
  };

  const submit = async details => {
    if (submitting.value || !dialog.value) return;
    const originalDialog = dialog.value;
    const { snapshot, action } = originalDialog;
    const command = {
      operation_id: crypto.randomUUID(),
      ticket_id: snapshot.ticket.ticket_id,
      expected_revision: snapshot.ticket.revision,
      action,
      ...details,
    };
    intent.value = command;
    dialog.value = null;
    submitting.value = true;
    error.value = '';
    const version = generation;
    operation.value = {
      state: 'queued',
      operation_id: command.operation_id,
      payload: command,
    };
    try {
      const { data } = await GgSupportStateAPI.confirm(
        toValue(conversationId),
        command
      );
      if (!disposed && version === generation) {
        intent.value = null;
        await apply(data.operation);
        await poll(command.operation_id);
      }
    } catch (exception) {
      if (!disposed && version === generation) {
        if (
          exception.response &&
          exception.response.status < 500 &&
          exception.response.status !== 408
        ) {
          operation.value = null;
          intent.value = null;
          error.value =
            exception.response.data.code || 'SUPPORT_STATE_UNAVAILABLE';
          dialog.value = {
            ...originalDialog,
            closingText: details.closing_text || originalDialog.closingText,
          };
        } else {
          error.value = 'STATUS_UNAVAILABLE';
          await poll(command.operation_id);
        }
      }
    } finally {
      if (!disposed && version === generation) submitting.value = false;
    }
  };

  const resume = async () => {
    if (submitting.value || !operation.value) return;
    submitting.value = true;
    const version = generation;
    error.value = '';
    try {
      const response = intent.value
        ? await GgSupportStateAPI.confirm(toValue(conversationId), intent.value)
        : await GgSupportStateAPI.resume(
            toValue(conversationId),
            operation.value.operation_id
          );
      if (!disposed && version === generation) {
        intent.value = null;
        await apply(response.data.operation);
        await poll(response.data.operation.operation_id);
      }
    } catch (exception) {
      if (!disposed && version === generation)
        error.value = exception.response?.data?.code || 'STATUS_UNAVAILABLE';
    } finally {
      if (!disposed && version === generation) submitting.value = false;
    }
  };

  watch(
    [
      () => toValue(conversationId),
      () => toValue(ticketId),
      () => toValue(enabled),
    ],
    async () => {
      generation += 1;
      const version = generation;
      clearTimeout(timer);
      lookup.abort();
      recovery.abort();
      current.value = null;
      operation.value = null;
      dialog.value = null;
      intent.value = null;
      submitting.value = false;
      if (toValue(enabled) && toValue(ticketId)) {
        await poll();
        if (!disposed && version === generation && !current.value)
          await refresh();
      }
    },
    { immediate: true }
  );
  onScopeDispose(() => {
    disposed = true;
    clearTimeout(timer);
  });

  return {
    current,
    operation,
    error,
    busy,
    pending,
    blocked,
    noteFailed,
    dialog,
    intent,
    refresh,
    open,
    submit,
    resume,
  };
}
